// ============================================
// moldClaw Billing Management Page (Polar)
// ============================================

/**
 * 🧑‍💻 SWE-3: 빌링 관리 페이지
 * 🎨 CX-1: 사용자 친화적 결제 UI
 * 🔧 POLAR: Polar 연동 (2026-03-16)
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePayment } from '../hooks/usePayment';
import { TIER_INFO } from '../hooks/useLicense';

// 플랜 정보
const PLAN_INFO = {
  free: { name: TIER_INFO.free.name, price: TIER_INFO.free.priceLabel },
  pro: { name: TIER_INFO.pro.name, price: TIER_INFO.pro.priceLabel },
  team: { name: TIER_INFO.team.name, price: TIER_INFO.team.priceLabel },
};

// 취소 사유
const CANCEL_REASONS = [
  { value: 'too_expensive', label: '비용이 너무 높아요' },
  { value: 'not_using', label: '서비스를 잘 사용하지 않아요' },
  { value: 'missing_features', label: '필요한 기능이 없어요' },
  { value: 'found_alternative', label: '다른 서비스를 찾았어요' },
  { value: 'technical_issues', label: '기술적 문제가 있어요' },
  { value: 'temporary', label: '일시적으로 사용을 중단해요' },
  { value: 'other', label: '기타' },
];

// FAQ
const BILLING_FAQ = [
  {
    q: '결제는 어떻게 이루어지나요?',
    a: '매월 구독 시작일에 등록된 결제 수단으로 자동 결제됩니다. 결제 3일 전 이메일로 알림을 보내드립니다.',
  },
  {
    q: '환불 정책은 어떻게 되나요?',
    a: '구독 시작 7일 이내 취소 시 전액 환불됩니다. 7일 이후에는 남은 기간에 대해 일할 계산으로 환불해드립니다.',
  },
  {
    q: '플랜 변경은 언제 적용되나요?',
    a: '업그레이드는 즉시 적용되며 차액만 결제됩니다. 다운그레이드는 현재 결제 기간 종료 후 적용됩니다.',
  },
  {
    q: '결제 실패 시 어떻게 되나요?',
    a: '결제 실패 시 3일간 재시도하며, 그래도 실패하면 서비스가 Free 플랜으로 전환됩니다. 결제 수단을 업데이트해주세요.',
  },
  {
    q: '영수증은 어디서 받나요?',
    a: '결제 수단 관리 버튼을 클릭하면 Polar 포털에서 모든 결제 내역과 영수증을 확인하고 다운로드할 수 있습니다.',
  },
];

export default function Billing() {
  const { user } = useAuth();
  const {
    isLoading,
    error,
    subscription,
    loadSubscription,
    checkout,
    cancelSubscription,
    manageSubscription,
    clearError,
    hasActiveSubscription,
    isSubscriptionCanceled,
  } = usePayment();
  
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonDetail, setCancelReasonDetail] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  useEffect(() => {
    if (user) loadSubscription();
  }, [user, loadSubscription]);
  
  useEffect(() => {
    if (error) {
      const timer = setTimeout(clearError, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);
  
  const handleManagePayment = async () => {
    setActionLoading('manage');
    try {
      await manageSubscription();
    } catch { /* handled in hook */ }
    setActionLoading(null);
  };
  
  const handleCancelSubscription = async () => {
    const reason = cancelReason === 'other' ? cancelReasonDetail : cancelReason;
    setActionLoading('cancel');
    const success = await cancelSubscription(reason);
    if (success) {
      setShowCancelModal(false);
      setCancelReason('');
      setCancelReasonDetail('');
    }
    setActionLoading(null);
  };
  
  const handleUpgrade = async (tier: 'pro' | 'team') => {
    setActionLoading(`upgrade-${tier}`);
    try {
      await checkout(tier);
    } catch { /* handled in hook */ }
    setActionLoading(null);
  };
  
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <p className="text-gray-400 mb-4">로그인이 필요합니다</p>
          <Link to="/login" className="text-space-blue-light hover:underline">로그인하기 →</Link>
        </div>
      </div>
    );
  }
  
  const currentTier = subscription?.tier || 'free';
  const planInfo = PLAN_INFO[currentTier as keyof typeof PLAN_INFO];
  
  const getDaysRemaining = () => {
    if (!subscription?.current_period_end) return null;
    const endDate = new Date(subscription.current_period_end);
    const diffDays = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };
  
  const daysRemaining = getDaysRemaining();
  
  const getStatus = () => {
    if (!subscription) return { label: '미구독', bgClass: 'bg-gray-500/20', textClass: 'text-gray-400' };
    if (isSubscriptionCanceled) return { label: '취소 예정', bgClass: 'bg-yellow-500/20', textClass: 'text-yellow-400' };
    if (subscription.status === 'active') return { label: '활성', bgClass: 'bg-green-500/20', textClass: 'text-green-400' };
    if (subscription.status === 'past_due') return { label: '결제 지연', bgClass: 'bg-red-500/20', textClass: 'text-red-400' };
    return { label: subscription.status, bgClass: 'bg-gray-500/20', textClass: 'text-gray-400' };
  };
  
  const status = getStatus();
  
  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* 에러 */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 flex justify-between">
            <span>{error}</span>
            <button onClick={clearError}>✕</button>
          </div>
        )}
        
        {/* 헤더 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">결제 관리</h1>
          <p className="mt-2 text-gray-400">구독을 관리하세요</p>
        </div>
        
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <div className="space-y-6">
            {/* 구독 상태 */}
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-400 mb-1">현재 플랜</p>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl font-bold text-white">{planInfo.name}</span>
                    <span className={`px-3 py-1 ${status.bgClass} ${status.textClass} rounded-full text-sm`}>
                      {status.label}
                    </span>
                  </div>
                  <p className="text-xl text-gray-300 mt-1">{planInfo.price}/월</p>
                </div>
                
                {subscription && currentTier !== 'free' && (
                  <div className="flex gap-4 flex-wrap">
                    <div className="px-4 py-3 bg-gray-700/50 rounded-lg">
                      <p className="text-xs text-gray-400">📅 구독 시작</p>
                      <p className="font-semibold text-white">{formatDate(subscription.current_period_start)}</p>
                    </div>
                    <div className={`px-4 py-3 rounded-lg ${isSubscriptionCanceled ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-gray-700/50'}`}>
                      <p className="text-xs text-gray-400">{isSubscriptionCanceled ? '⚠️ 만료 예정' : '🔄 다음 결제'}</p>
                      <p className={`font-semibold ${isSubscriptionCanceled ? 'text-yellow-400' : 'text-white'}`}>
                        {formatDate(subscription.current_period_end)}
                      </p>
                    </div>
                    {daysRemaining !== null && (
                      <div className={`px-4 py-3 rounded-lg ${daysRemaining <= 7 ? 'bg-orange-500/10 border border-orange-500/30' : 'bg-gray-700/50'}`}>
                        <p className="text-xs text-gray-400">⏳ 남은 일수</p>
                        <p className={`font-semibold ${daysRemaining <= 7 ? 'text-orange-400' : 'text-white'}`}>{daysRemaining}일</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* 경고 */}
              {isSubscriptionCanceled && (
                <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-3">
                  <span className="text-xl">⚠️</span>
                  <div>
                    <p className="text-yellow-400 font-medium">구독 취소가 예약되어 있습니다</p>
                    <p className="text-yellow-400/70 text-sm">{formatDate(subscription?.current_period_end)}까지 이용 가능합니다.</p>
                  </div>
                </div>
              )}
              
              {subscription?.status === 'past_due' && (
                <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
                  <span className="text-xl">🚨</span>
                  <div>
                    <p className="text-red-400 font-medium">결제에 문제가 발생했습니다</p>
                    <button onClick={handleManagePayment} className="text-red-400 text-sm hover:underline mt-1">
                      결제 수단 확인 →
                    </button>
                  </div>
                </div>
              )}
              
              {/* 결제 관리 버튼 */}
              {hasActiveSubscription && (
                <div className="mt-6">
                  <button
                    onClick={handleManagePayment}
                    disabled={actionLoading === 'manage'}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {actionLoading === 'manage' ? <Spinner /> : '💳'} 결제 수단 관리
                  </button>
                </div>
              )}
            </div>
            
            {/* 플랜 변경 */}
            <div className="bg-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">플랜 변경</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <PlanCard
                  name="Free"
                  price="₩0"
                  features={['기기 1대', '기본 기능']}
                  isCurrent={currentTier === 'free'}
                  buttonLabel={currentTier === 'free' ? '현재 플랜' : '취소 시 전환'}
                />
                <PlanCard
                  name="Pro"
                  price="₩19,900"
                  features={['기기 3대', '모든 기능', '우선 지원']}
                  isCurrent={currentTier === 'pro'}
                  onSelect={currentTier === 'free' ? () => handleUpgrade('pro') : undefined}
                  buttonLabel={currentTier === 'pro' ? '현재 플랜' : currentTier === 'free' ? '업그레이드' : '다운그레이드'}
                  variant={currentTier === 'free' ? 'upgrade' : 'default'}
                  loading={actionLoading === 'upgrade-pro'}
                  popular
                />
                <PlanCard
                  name="Team"
                  price="₩32,900"
                  features={['기기 5대', '팀 관리', '전담 지원']}
                  isCurrent={currentTier === 'team'}
                  onSelect={currentTier !== 'team' ? () => handleUpgrade('team') : undefined}
                  buttonLabel={currentTier === 'team' ? '현재 플랜' : '업그레이드'}
                  variant={currentTier !== 'team' ? 'upgrade' : 'default'}
                  loading={actionLoading === 'upgrade-team'}
                />
              </div>
            </div>
            
            {/* 구독 취소 */}
            {hasActiveSubscription && !isSubscriptionCanceled && currentTier !== 'free' && (
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">구독 취소</h3>
                <p className="text-gray-400 text-sm mb-4">취소해도 현재 기간 종료까지 이용 가능합니다.</p>
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="px-4 py-2 border border-red-500 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  구독 취소
                </button>
              </div>
            )}
            
            {/* FAQ */}
            <FAQSection />
            
            {/* 고객 지원 */}
            <div className="bg-gradient-to-br from-space-blue/10 to-purple-500/10 rounded-xl p-6 border border-space-blue/20">
              <h4 className="text-white font-semibold mb-2">도움이 필요하신가요?</h4>
              <p className="text-gray-400 text-sm mb-4">결제 관련 문의는 언제든 연락주세요.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="mailto:support@moldclaw.com" className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
                  📧 support@moldclaw.com
                </a>
                <a href="https://docs.moldclaw.com/billing" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
                  📖 결제 가이드
                </a>
              </div>
            </div>
          </div>
        )}
        
        {/* 푸터 - 신뢰 요소 */}
        <div className="mt-12 pt-8 border-t border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <span className="flex items-center gap-1"><span className="text-green-400">🔒</span> SSL 암호화</span>
            <span className="flex items-center gap-1"><span className="text-blue-400">🛡️</span> PCI DSS 준수</span>
            <span className="flex items-center gap-1"><span className="text-purple-400">✓</span> Polar 결제</span>
          </div>
          <div className="flex gap-4 flex-wrap justify-center">
            <Link to="/terms" className="hover:text-white transition-colors">이용약관</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">개인정보처리방침</Link>
            <Link to="/refund-policy" className="hover:text-white transition-colors">환불정책</Link>
          </div>
        </div>
        
        {/* 취소 모달 */}
        {showCancelModal && (
          <CancelModal
            onConfirm={handleCancelSubscription}
            onClose={() => setShowCancelModal(false)}
            reason={cancelReason}
            setReason={setCancelReason}
            reasonDetail={cancelReasonDetail}
            setReasonDetail={setCancelReasonDetail}
            isLoading={actionLoading === 'cancel'}
            periodEnd={subscription?.current_period_end}
            currentTier={currentTier}
          />
        )}
      </div>
    </div>
  );
}

// ============================================
// Components
// ============================================

function PlanCard({ name, price, features, isCurrent, onSelect, buttonLabel, variant = 'default', loading, popular }: {
  name: string;
  price: string;
  features: string[];
  isCurrent: boolean;
  onSelect?: () => void;
  buttonLabel: string;
  variant?: 'default' | 'upgrade';
  loading?: boolean;
  popular?: boolean;
}) {
  return (
    <div className={`relative p-4 rounded-lg border ${isCurrent ? 'border-space-blue bg-space-blue/10' : 'border-gray-700 bg-gray-700/30'}`}>
      {popular && !isCurrent && (
        <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-space-blue text-white text-xs rounded-full">인기</span>
      )}
      <h4 className="text-white font-semibold">{name}</h4>
      <p className="text-xl font-bold text-white mt-1">{price}<span className="text-sm text-gray-400">/월</span></p>
      <ul className="mt-3 space-y-1">
        {features.map((f, i) => (
          <li key={i} className="text-gray-400 text-sm flex items-center gap-2">
            <span className="text-green-400">✓</span> {f}
          </li>
        ))}
      </ul>
      <button
        onClick={onSelect}
        disabled={isCurrent || !onSelect || loading}
        className={`w-full mt-4 py-2 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
          isCurrent ? 'bg-gray-600 text-gray-400 cursor-not-allowed' :
          variant === 'upgrade' ? 'bg-space-blue hover:bg-space-blue/80 text-white' :
          'bg-gray-600 hover:bg-gray-500 text-white'
        } disabled:opacity-50`}
      >
        {loading ? <><Spinner /> 처리 중...</> : buttonLabel}
      </button>
    </div>
  );
}

function CancelModal({ onConfirm, onClose, reason, setReason, reasonDetail, setReasonDetail, isLoading, periodEnd, currentTier }: {
  onConfirm: () => void;
  onClose: () => void;
  reason: string;
  setReason: (r: string) => void;
  reasonDetail: string;
  setReasonDetail: (r: string) => void;
  isLoading: boolean;
  periodEnd?: string;
  currentTier: string;
}) {
  const planName = PLAN_INFO[currentTier as keyof typeof PLAN_INFO]?.name || 'Pro';
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-gray-800 rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between mb-4">
          <h3 className="text-xl font-bold text-white">구독을 취소하시겠어요?</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>
        
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-400 font-medium mb-2">⚠️ 취소하면 잃게 되는 혜택:</p>
          <ul className="text-red-400/80 text-sm space-y-1">
            <li>• 등록 기기 수 제한 (3대 → 1대)</li>
            <li>• 고급 기능 및 우선 지원</li>
          </ul>
        </div>
        
        <p className="text-gray-400 mb-4">
          취소해도 <span className="text-white font-medium">{formatDate(periodEnd)}</span>까지 {planName} 기능을 이용할 수 있습니다.
        </p>
        
        <div className="mb-6">
          <p className="text-sm text-gray-400 mb-3">취소 사유</p>
          <div className="space-y-2">
            {CANCEL_REASONS.map((opt) => (
              <label key={opt.value} className={`flex items-center p-3 rounded-lg cursor-pointer ${reason === opt.value ? 'bg-space-blue/20 border border-space-blue' : 'bg-gray-700/50 hover:bg-gray-700'}`}>
                <input type="radio" name="reason" value={opt.value} checked={reason === opt.value} onChange={(e) => setReason(e.target.value)} className="sr-only" />
                <span className={`w-4 h-4 rounded-full border-2 mr-3 ${reason === opt.value ? 'border-space-blue bg-space-blue' : 'border-gray-500'}`}>
                  {reason === opt.value && <span className="block w-2 h-2 bg-white rounded-full m-auto mt-0.5" />}
                </span>
                <span className="text-white">{opt.label}</span>
              </label>
            ))}
          </div>
          {reason === 'other' && (
            <textarea
              value={reasonDetail}
              onChange={(e) => setReasonDetail(e.target.value)}
              placeholder="취소 사유를 입력해주세요"
              className="w-full mt-3 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-space-blue"
              rows={3}
            />
          )}
        </div>
        
        <div className="flex gap-4">
          <button onClick={onClose} className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium">돌아가기</button>
          <button
            onClick={onConfirm}
            disabled={isLoading || !reason}
            className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading ? <><Spinner /> 처리 중...</> : '구독 취소'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  
  return (
    <div className="bg-gray-800 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <span>❓</span> 자주 묻는 질문
      </h3>
      <div className="space-y-2">
        {BILLING_FAQ.map((faq, i) => (
          <div key={i} className="border border-gray-700 rounded-lg overflow-hidden">
            <button
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-700/50 transition-colors"
            >
              <span className="text-white font-medium">{faq.q}</span>
              <span className={`text-gray-400 transform transition-transform ${openIndex === i ? 'rotate-180' : ''}`}>▼</span>
            </button>
            {openIndex === i && (
              <div className="px-4 pb-4">
                <p className="text-gray-400 text-sm">{faq.a}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-gray-800 rounded-xl p-6 animate-pulse">
          <div className="h-4 bg-gray-700 rounded w-1/3 mb-4" />
          <div className="h-8 bg-gray-700 rounded w-1/2" />
        </div>
      ))}
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function formatDate(d?: string): string {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
}
